import { useAppRouterInterface, useAppRouterState } from '@/app/addRouter';
import Stack from '@/components/Layout/Stack/Stack';
import { StackDrawer } from '@/components/Layout/StackDrawer/StackDrawer';
import { AppSidebar } from '@/components/Sidebar/app-sidebar';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/Sidebar/sidebar';
import { useSidebar } from '@/components/ui/Sidebar/SidebarContextProps';
import { appLogger } from '@/config/logging';
import { Breakpoint, useBreakpoint } from '@/hooks/use-breakpoint';
import { useIsMobile } from '@/hooks/use-mobile';
import { useWidth } from '@/hooks/use-width';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function App() {
  const { open } = useSidebar();
  const breakpoint = useBreakpoint();
  const width = useWidth();
  const isMobile = useIsMobile();
  const appRouter = useAppRouterInterface();
  const [primaryStackPath, secondaryStackPath] =
    useAppRouterState().drillDownPaths;

  const hasSecondaryStack = !!secondaryStackPath;

  // Animate drawer open/close while keeping content mounted during transitions.
  // - Opening: mount at width 0, then expand on next frame
  // - Closing: collapse first, then unmount after transition completes
  const [deferredStack, setDeferredStack] = useState(secondaryStackPath);
  const [drawerAnimState, setDrawerAnimState] = useState(!!secondaryStackPath);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(timer.current);
    let raf: number | undefined;
    if (secondaryStackPath) {
      setDeferredStack(secondaryStackPath);
      raf = requestAnimationFrame(() => setDrawerAnimState(true));
    } else {
      setDrawerAnimState(false);
      timer.current = setTimeout(() => setDeferredStack(null), 300);
    }
    return () => {
      clearTimeout(timer.current);
      if (raf !== undefined) cancelAnimationFrame(raf);
    };
  }, [secondaryStackPath]);

  // The stack to render inside the drawer (stays during close animation)
  const drawerStack = secondaryStackPath ?? deferredStack;

  const { primaryPanelCnt, canFitDrawer } = useMemo(() => {
    const sidebarWidth = isMobile
      ? 0
      : open
        ? 224 /* expanded */
        : 48; /* collapsed */

    /** Tailwind v4 --spacing unit in px (0.25rem = 4px at default font size) */
    const SPCNG = 4;
    /** Must match StackDrawer CSS: calc(var(--container-lg) + var(--spacing)*4) = 512 + 16 = 528 */
    const DRAWER_W = 512 + SPCNG * 4;
    const MIN_PRIMARY = 512 + SPCNG * 2;

    // Pure viewport check — not tied to hasSecondaryStack so close
    // animations can play without canFitDrawer flipping mid-transition
    const totalWidth = width - sidebarWidth;
    const canFitDrawer = totalWidth >= MIN_PRIMARY + DRAWER_W;

    const drawerWidth = hasSecondaryStack && canFitDrawer ? DRAWER_W : 0;
    const availableWidth = totalWidth - drawerWidth;

    const MIN_COMPACT = 384 + SPCNG;
    const MIN_FULL = 512 + SPCNG;
    const primaryPanelCnt =
      availableWidth >= MIN_COMPACT + MIN_FULL + SPCNG * 2 ? 2 : 1;

    return { primaryPanelCnt, canFitDrawer };
  }, [width, open, isMobile, hasSecondaryStack]);

  const drawerOpen = drawerAnimState && canFitDrawer;
  const overlayOpen = hasSecondaryStack && !canFitDrawer;
  const drawerMode = canFitDrawer ? 'inline' : 'overlay';

  // Clicking the overlay backdrop dismisses the secondary stack
  const handleBackdropClick = useCallback(() => {
    appRouter.dropStack(1);
  }, [appRouter]);

  useEffect(() => {
    appLogger.info('Entered breakpoint {breakpoint} ({width})', {
      breakpoint: Breakpoint[breakpoint],
      width: breakpoint,
    });
  }, [breakpoint]);

  useEffect(() => {
    appLogger.info('App mounted');
    return () => {
      appLogger.info('App unmounted');
    };
  }, []);

  const primaryStack = (primaryStackPath && (
    <Stack
      {...primaryStackPath}
      key={primaryStackPath.key}
      panelCnt={Math.min(primaryPanelCnt, 2)}
      manageDocumentTitle
    />
  )) || (
    <div className="group/stack mb-2 flex grow flex-col px-1 py-0 md:px-2" />
  );

  const secondaryStack = drawerStack && (
    <Stack {...drawerStack} key={drawerStack.key} panelCnt={1} stackIdx={1} />
  );

  return (
    <>
      <AppSidebar />
      <SidebarInset>
        <main
          className={cn(
            'min-h-[100vh] w-full justify-between md:flex [&>*]:shrink-1',
            'bg-muted',
          )}
        >
          {primaryStack}

          {drawerStack && (
            <StackDrawer
              open={drawerOpen || overlayOpen}
              mode={drawerMode}
              onBackdropClick={handleBackdropClick}
            >
              {secondaryStack}
            </StackDrawer>
          )}
        </main>

        {/* Floating menu button for mobile - bottom-left */}
        <SidebarTrigger
          className="bg-primary text-primary-foreground hover:bg-primary/90 fixed bottom-4 left-4
            z-50 size-14 rounded-full shadow-lg md:hidden"
        />
      </SidebarInset>
    </>
  );
}

function WrappedApp() {
  return (
    <SidebarProvider>
      <App />
    </SidebarProvider>
  );
}

export default WrappedApp;
